import NiceModal from '@ebay/nice-modal-react';

import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Menu from 'components/global/Menu';
import NetworkStatus from 'components/global/NetworkStatus';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';
import SearchOrder from 'components/layout/TopBar/SearchOrder';

import useAccessControl from 'hooks/useAccessControl';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useChangeLanguage from 'hooks/useChangeLanguage';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';

import ChargeAccountModal from 'pages/Topbar/ChargeAccount/ChargeAccountModal';
import Credit from 'pages/Topbar/Credit/Credit';
import NotificationPopup from 'pages/Topbar/Notifications/NotificationPopup';

import { PROFILE } from 'services/consts';

import { toogleSideBar } from 'stores/layoutSlice';

import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';
import PATHS from 'utils/paths';
import { getMinusCreditAlertState } from 'utils/utils';

import { faChevronDown, faSearch, faWallet } from '@fortawesome/pro-regular-svg-icons';
import { faBarsSort, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faBell } from '@fortawesome/pro-thin-svg-icons';

import * as S from './TopBar.styled';

const TopBar = () => {
	const dispatch = useDispatch();
	const isAuthorized = useAccessControl();
	const sideBarOpen = useSelector((state) => state.layout.sideBarOpen);
	const { pathname, state } = useLocation();
	const { assignedWarehouses, assignedBranches, userType, lastTransactionDate, remainingAmount } = useSelector(
		(state) => state.user
	);
	const showSearchOrder = useMemo(() => {
		if (userType === 'branchUser' && assignedBranches?.length === 0) {
			return false;
		} else if (userType === 'warehouseUser' && assignedWarehouses?.length === 0) {
			return false;
		} else {
			return true;
		}
	}, [assignedWarehouses, assignedBranches, userType]);

	const [notificationButtonRef, setNotificationButtonRef] = useState(null);
	const [otoCreditButtonRef, setOtoCreditButtonRef] = useState(null);
	const [languageButtonRef, setLanguageButtonRef] = useState(null);
	const { unreadCount } = useSelector((state) => state.notifications);
	const { showModal } = useModalRouter();
	const navigate = useNavigate();
	const localization = useSelector((state) => state.layout.localization);
	const changeLanguage = useChangeLanguage();
	const axios = useAxiosPrivate();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const [isSearchOrderOpen, setIsSearchOrderOpen] = useState(false);

	useEffect(() => {
		if (state?.from === 'login') {
			const minusCreditAlert = getMinusCreditAlertState(remainingAmount, lastTransactionDate);
			if (remainingAmount < 0 && minusCreditAlert.show) {
				NiceModal.show(ChargeAccountModal, {
					defaultSelectedAmount: -remainingAmount
				});
				localStorage.removeItem('isFirstLogin');
			}
		}
	}, []);

	return (
		<S.TopBarContainer isSearchOrderOpen={isSearchOrderOpen}>
			<S.TopBar>
				<S.TopbarLeftSide>
					<S.TopbarToggleBtn isopen={sideBarOpen}>
						<Button
							className="sidebarToggleIcon"
							variant="borderless"
							iconButton
							onClick={() => dispatch(toogleSideBar())}>
							<Icon icon={faBarsSort} />
						</Button>
					</S.TopbarToggleBtn>
				</S.TopbarLeftSide>

				<S.TopbarRightSide>
					<div style={{ flexGrow: 1 }} />
					<NetworkStatus />

					<Authorization path="/topbar/button/searchOrder">
						{showSearchOrder &&
							(isMobile ? (
								<Button
									variant="borderless"
									icon={faSearch}
									iconButton
									iconSize="large"
									onClick={() => setIsSearchOrderOpen(!isSearchOrderOpen)}
								/>
							) : (
								<SearchOrder />
							))}
					</Authorization>

					{isAuthorized(PATHS.CREATE_SHIPMENT) ? (
						<Button
							variant="outline"
							size="medium"
							label="createShipment"
							icon={faPlus}
							onClick={() => {
								if (pathname !== PATHS.CREATE_SHIPMENT) {
									navigate(PATHS.CREATE_SHIPMENT);
								}
							}}
							className="add-order-button topbar-button"
						/>
					) : isAuthorized('/modal/add-order-form') ? (
						<Button
							variant="outline"
							size="medium"
							label="addOrder"
							icon={faPlus}
							onClick={() => showModal('add-order-form')}
							className="add-order-button topbar-button"
						/>
					) : null}

					<Authorization path="/topbar/button/otoCredit">
						<S.DashedButton
							ref={setOtoCreditButtonRef}
							variant="dashed"
							size="medium"
							className="topbar-button">
							{!isMobile && <Icon icon={faWallet} size="medium" />}
							<Typography variant="p2" fontWeight="700" withCurrency className="remaining-amount-text">
								{remainingAmount}
							</Typography>
							<Icon icon={faChevronDown} size="small" />
						</S.DashedButton>
						{otoCreditButtonRef && (
							<Popper
								wrapperId="creditPopper"
								closeTimeout={500}
								hoverMode
								refElement={otoCreditButtonRef}
								placement="bottom-end"
								offsetY={8}>
								{(closePopper) => (
									<Credit key="OtoCredit" title="lastTransactions" closePopper={closePopper} />
								)}
							</Popper>
						)}
					</Authorization>
					<Authorization path="/notifications">
						<Badge
							anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
							className="notification-badge"
							variant="notification"
							text={(unreadCount > 999 ? '999+' : unreadCount) || (unreadCount === 0 && '')}>
							<Button
								ref={setNotificationButtonRef}
								icon={faBell}
								iconSize="large"
								iconButton
								variant="borderless"
								className="notification-icon"
								css={S.NotificationButtonCss}
							/>
						</Badge>

						{notificationButtonRef && (
							<Popper refElement={notificationButtonRef} placement="bottom-end" offsetY={14} showArrow>
								{(closePopper) => <NotificationPopup closePopper={closePopper} />}
							</Popper>
						)}
					</Authorization>
					<Button variant="borderless" ref={setLanguageButtonRef} size="medium">
						<Image url={localization.icon} width="25px" height="20px" style={{ borderRadius: '4px' }} />
						<Icon icon={faChevronDown} />
					</Button>
					{languageButtonRef && (
						<Menu
							buttonRef={languageButtonRef}
							menuItems={languages.map((language) => ({
								label: language.name,
								img: language.icon,
								imgProps: { width: '25px', height: '20px', style: { borderRadius: '4px' } },
								selected: language.code === localization.code,
								action: () => {
									changeLanguage(language.code);
									axios.post(PROFILE.UPDATE_USER_DETAILS, {
										locale: language.systemCode
									});
								}
							}))}
						/>
					)}
				</S.TopbarRightSide>
			</S.TopBar>
			{showSearchOrder && isMobile && isSearchOrderOpen && (
				<Authorization path="/topbar/button/searchOrder">
					<S.SearchBarContainer>
						<SearchOrder />
					</S.SearchBarContainer>
				</Authorization>
			)}
		</S.TopBarContainer>
	);
};

export default memo(TopBar);
